import { BibleBook, bibleBooks } from '../data/bible_data';
import { getMaxVerse, normalizeBookName } from './bible-limits';

// Define available translations for saintebible.com
export const SAINTEBIBLE_TRANSLATIONS = {
  lsg: 'lsg',    // Louis Segond
  mar: 'mar',    // Martin
  drb: 'drb',    // Darby
} 

export type TranslationType = typeof SAINTEBIBLE_TRANSLATIONS[keyof typeof SAINTEBIBLE_TRANSLATIONS] | 'aelf';

export function getBaseUrlForTranslation(translation: TranslationType, book: string, chapter: string) {
  console.log('Generating base URL for:', translation, book, chapter);

  const translationsConfig: {
    [key: string]: {
      baseUrl: string;
      getBookId: (book: string, bibleBooks: BibleBook[]) => string;
      chapterFormat: (chapter: string) => string;
    };
  } = {
    'aelf': {
      baseUrl: 'https://www.aelf.org/bible/',
      getBookId: (book: string, bibleBooks: BibleBook[]) => {
        const foundBook = bibleBooks.find(b => b.abbreviations.includes(book.toLowerCase()));
        return foundBook ? foundBook.id.toLowerCase() : ''; // Return uppercase ID or empty string
      },
      chapterFormat: (chapter: string) => chapter,
    },
    ...Object.values(SAINTEBIBLE_TRANSLATIONS).reduce((acc, translationCode) => ({
      ...acc,
      [translationCode]: {
        baseUrl: `https://saintebible.com/${translationCode}/`,
        getBookId: (book: string, bibleBooks: BibleBook[]) => {
          const foundBook = bibleBooks.find(b => b.abbreviations.includes(book.toLowerCase()));
          if (!foundBook) return '';
          const bookMap: { [key: string]: string } = {
            'gn': 'genesis', 'ex': 'exodus', 'lv': 'leviticus', 'nb': 'numbers', 'dt': 'deuteronomy',
            'js': 'joshua', 'jg': 'judges', 'rt': 'ruth', '1s': '1samuel', '2s': '2samuel',
            '1r': '1kings', '2r': '2kings', '1ch': '1chronicles', '2ch': '2chronicles', 'ez': 'ezra',
            'nh': 'nehemiah', 'et': 'esther', 'jb': 'job', 'ps': 'psalms', 'pr': 'proverbs',
            'ec': 'ecclesiastes', 'ct': 'songofsongs', 'is': 'isaiah', 'jr': 'jeremiah', 'lm': 'lamentations',
            'ezk': 'ezekiel', 'dn': 'daniel', 'os': 'hosea', 'jl': 'joel', 'am': 'amos', 'ab': 'obadiah',
            'jn': 'john', 'mi': 'micah', 'na': 'nahum', 'ha': 'habakkuk', 'zp': 'zephaniah',
            'hg': 'haggai', 'zc': 'zechariah', 'ml': 'malachi', 'mt': 'matthew', 'mc': 'mark',
            'lc': 'luke', 'ac': 'acts', 'rm': 'romans', '1co': '1corinthians', '2co': '2corinthians',
            'ga': 'galatians', 'ep': 'ephesians', 'ph': 'philippians', 'cl': 'colossians',
            '1th': '1thessalonians', '2th': '2thessalonians', '1tm': '1timothy', '2tm': '2timothy',
            'tt': 'titus', 'phm': 'philemon', 'he': 'hebrews', 'jm': 'james', '1p': '1peter',
            '2p': '2peter', '1jn': '1john', '2jn': '2john', '3jn': '3john', 'ju': 'jude', 'rv': 'revelation',
          };
          return bookMap[foundBook.id] || foundBook.name.toLowerCase();
        },
        chapterFormat: (chapter: string) => `${chapter}.htm`,
      },
    }), {})
  };
console.log('Translations:', translation);
  const translationConfig = translationsConfig[translation];
  console.log('Translation config:', translationConfig);
  if (!translationConfig) {
    return '';
  }

  const { baseUrl, getBookId, chapterFormat } = translationConfig;

  const formattedBook = getBookId(book, bibleBooks); // Pass bibleBooks here
  console.log('Formatted book:', formattedBook);
  const formattedChapter = chapterFormat(chapter);
  console.log('Formatted chapter:', formattedChapter);

  return `${baseUrl}${formattedBook}/${formattedChapter}`;
};

export default getBaseUrlForTranslation;

/**
 * Convert an evangile reference string into a bible4.me URL path.
 *
 * Handles:
 *   - Double/single surrounding parentheses: ((Mt 5,1—7,29)) → stripped
 *   - Trailing letter suffixes on verse numbers: 4a → 4
 *   - Em dash cross-chapter range (—): Book Ch,V—Ch2,V2 → book/ch/v-ch2
 *   - En dash cross-chapter range (–): Book Ch,V–Ch2,V2 → book/ch/v-maxVerseOfCh
 *   - Same-chapter range / single verse: existing comma→slash logic
 */
export function buildReferencePath(reference: string): string {
  if (!reference) return '';

  // Strip surrounding parentheses (single or double)
  let ref = reference.replace(/^\(+|\)+$/g, '').trim();

  // Strip trailing lowercase letter suffixes on verse numbers (e.g. "4a" → "4")
  ref = ref.replace(/(\d)[a-z]+\b/g, '$1');

  // Em dash cross-chapter range: "Mt 5,1—7,29" (spaces around comma optional)
  const emDash = ref.match(/^(.+?)\s+(\d+)\s*,\s*(\d+)\s*—\s*(\d+)\s*,\s*(\d+)$/);
  if (emDash) {
    const [, book, startChapter, startVerse, endChapter] = emDash;
    const bookPath = book.toLowerCase().replace(/\s+/g, '');
    return `${bookPath}/${startChapter}/${startVerse}-${endChapter}`;
  }

  // En dash or hyphen cross-chapter: "Jn 15, 26-16-4", "Jn 15,26–16,4", etc.
  // \s*,\s* allows spaces around the chapter,verse comma.
  // Two-part end (chapter + verse separated by [-,]) avoids matching same-chapter ranges like "Mt 5,1-7".
  const crossChapter = ref.match(/^(.+?)\s+(\d+)\s*,\s*(\d+)\s*[-–]\s*(\d+)[-,](\d+)$/);
  if (crossChapter) {
    const [, book, startChapter, startVerse] = crossChapter;
    const bookPath = book.toLowerCase().replace(/\s+/g, '');
    const normalizedBook = normalizeBookName(book, 'fr');
    const maxVerse = getMaxVerse(normalizedBook, parseInt(startChapter, 10), 'catholic')
                  ?? getMaxVerse(normalizedBook, parseInt(startChapter, 10), 'protestant');
    if (maxVerse) {
      return `${bookPath}/${startChapter}/${startVerse}-${maxVerse}`;
    }
    return `${bookPath}/${startChapter}/${startVerse}`;
  }

  // Same-chapter range or single verse: existing logic
  return ref
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(',', '/')
    .replace(/^(\d?[a-z]+)(\d)/, '$1/$2');
}